import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../lib/lang";
import { useSeo } from "../lib/seo";

// Reveal-on-scroll helper for editorial fade-ins
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".about-page .reveal-host h1, .about-page .reveal-host .statement, .about-page .cta h2, .about-page .intro, .about-page .project-title, .about-page .clients, .about-page .belief h3, .about-page .service h3, .about-page .transition-line, .about-page .method-type, .about-page .split-card h3, .about-page .section-chapter");
    els.forEach((el) => el.classList.add("reveal"));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in-view"); io.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: "0px 0px -40px 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

export default function AboutPage() {
  const { t, lang } = useLang();
  useSeo({
    title: lang === "es"
      ? "Brand & Behavior — Brand Strategy & Brand Development Platform"
      : "Brand & Behavior — Brand Strategy & Brand Development Platform",
    description: lang === "es"
      ? "Enfoque, método y experiencia. Brand & Behavior es una plataforma productizada de brand strategy y brand development, dirigida por Wilhelm Noriega."
      : "Approach, method, and selected experience. Brand & Behavior is a productized brand strategy and development platform led by Wilhelm Noriega.",
    path: lang === "es" ? "/es/about" : "/about",
  });
  useReveal();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, []);

  return (
    <main className="app-main about-page reveal-host" data-testid="about-page">
      <div className="wrap">
        <section id="approach">
          <div className="section-chapter"><span className="roman">I</span><span className="dot"></span></div>
          <div className="section-head">
            <h2>{t("The Approach", "El enfoque")}</h2>
            <p className="statement">{t("In an age of infinite output, the advantage is ", "En una era de producción infinita, la verdadera ventaja es el ")}<em className="it">{t("judgment", "criterio")}</em>.</p>
          </div>
          <div className="services">
            {[
              { num: "01 / Brand Strategy", numEs: "01 / Estrategia de marca", h: "Define what the brand should ", he: "become", hes2: "Definir en qué ", heEs: "debe convertirse", hesEs2: " la marca.", p: "Positioning, narrative, architecture, audience definition, and the strategic choices that give a brand direction.", pEs: "Posicionamiento, narrativa, arquitectura, definición de audiencia y las decisiones estratégicas que dan dirección a una marca.", mark: "01" },
              { num: "02 / Brand Management", numEs: "02 / Gestión de marca", h: "Keep the brand ", he: "coherent", hes2: " over time.", heEs: "coherente", hesEs2: " en el tiempo.", hStartEs: "Mantener la marca ", p: "Ongoing senior brand direction across communication, priorities, launches, partnerships, and market presence.", pEs: "Dirección de marca senior continua en comunicación, prioridades, lanzamientos, alianzas y presencia de mercado.", mark: "02", bg: "rgba(0,0,0,.035)" },
              { num: "03 / Go-to-Market", numEs: "03 / Salida al mercado", h: "Connect brand direction to commercial ", he: "action", hes2: ".", heEs: "acción", hesEs2: " comercial.", hStartEs: "Conectar la dirección de marca con la ", p: "Market entry, launch roadmaps, messaging systems, growth priorities, and category-specific activation logic.", pEs: "Entrada al mercado, hojas de ruta de lanzamiento, sistemas de mensajes, prioridades de crecimiento y lógica de activación por categoría.", mark: "03" },
              { num: "04 / Creative Direction", numEs: "04 / Dirección creativa", h: "Turn creativity into ", he: "brand universes", hes2: ".", heEs: "universos de marca", hesEs2: ".", hStartEs: "Convertir la creatividad en ", p: "Creative direction that translates strategy into brand systems, campaigns and universes people want to belong to.", pEs: "Dirección creativa que traduce la estrategia en sistemas de marca, campañas y universos a los que la gente quiere pertenecer.", mark: "04" },
            ].map((s, i) => (
                <article className="service" key={s.mark} style={s.bg ? { background: s.bg } : undefined}>
                <span className="num">{t(s.num, s.numEs)}</span>
                <div>
                  <h3>
                    {i === 0
                      ? <>{t("Define what the brand should ", "Definir en qué ")}<em className="it">{t("become", "debe convertirse")}</em>{t(".", " la marca.")}</>
                      : i === 1
                      ? <>{t("Keep the brand ", "Mantener la marca ")}<em className="it">{t("coherent", "coherente")}</em>{t(" over time.", " en el tiempo.")}</>
                      : i === 2
                      ? <>{t("Connect brand direction to commercial ", "Conectar la dirección de marca con la ")}<em className="it">{t("action", "acción")}</em>{t(".", " comercial.")}</>
                      : <>{t("Turn ambition into ", "Convertir la ambición en ")}<em className="it">{t("belief", "convicción")}</em>.</>
                    }
                  </h3>
                  <div className="service-mark">{s.mark}</div>
                  <p>{t(s.p, s.pEs)}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="dark" id="pov">
          <div className="section-chapter"><span className="roman">II</span><span className="dot"></span></div>
          <div className="section-head">
            <h2>{t("Point of View", "Punto de vista")}</h2>
            <p className="statement">
              {t("Brands do not fail because they lack content. They fail because the ", "Las marcas no fracasan por falta de contenido. Fracasan cuando la ")}
              <em className="it">{t("direction", "dirección")}</em>
              {t(" is unclear.", " pierde claridad.")}
            </p>
          </div>
          <p className="small" style={{ maxWidth: 860, margin: "-18px 0 42px", fontSize: 18, lineHeight: 1.45 }}>
            {t("In a saturated and competitive market where execution is increasingly automated, the advantage is no longer production alone. It is ", "En un mercado saturado y competitivo donde la ejecución es cada vez más automatizada, la ventaja ya no es solo la producción. Es el ")}
            <em className="it">{t("judgment", "criterio")}</em>.
          </p>
          <div className="beliefs">
            <article className="belief">
              <span className="num">{t("Belief 01", "Convicción 01")}</span>
              <h3>{t("Strategy is the ", "La estrategia es la ")}<em className="it">{t("discipline", "disciplina")}</em>{t(" of deciding before doing.", " de decidir antes de hacer.")}</h3>
              <p>{t("Before a logo, campaign, deck, launch, or website, there must be a clear answer to what the brand is, who it is for, why it matters, and what it should refuse.", "Antes de un logo, una campaña, un deck, un lanzamiento o una web, debe haber una respuesta clara sobre qué es la marca, a quién se dirige, por qué importa y qué debe rechazar.")}</p>
            </article>
            <article className="belief">
              <span className="num">{t("Belief 02", "Convicción 02")}</span>
              <h3>{t("Brand management is becoming a ", "La gestión de marca se está convirtiendo en una necesidad de liderazgo ")}<em className="it">{t("senior", "senior")}</em>{t(" leadership need.", ".")}</h3>
              <p>{t("Companies need more than periodic rebrands. They need ongoing judgment to keep the brand coherent as the business grows, shifts, expands, and enters culture.", "Las compañías necesitan más que rebrands periódicos. Necesitan criterio continuo para mantener la marca coherente mientras el negocio crece, cambia, se expande y entra en la cultura.")}</p>
            </article>
          </div>
        </section>

        <section id="method">
          <div className="section-chapter"><span className="roman">III</span><span className="dot"></span></div>
          <div className="section-head">
            <h2>{t("Method", "Método")}</h2>
            <p className="statement">{t("Read the ", "Leer el ")}<em className="it">{t("context", "contexto")}</em>. {t("Define the ", "Definir la ")}<em className="it">{t("direction", "dirección")}</em>.</p>
          </div>
          <div className="method-editorial">
            <div className="method-canvas" aria-label="Editorial abstract method visual">
              <div className="method-kicker">{t("Strategic operating system", "Sistema operativo estratégico")}</div>
              <div className="method-type">{t("READ", "LEER")}<br/>{t("DEFINE", "DEFINIR")}<br/>{t("GUIDE", "GUIAR")}</div>
              <div className="method-axis"><span>{t("Context", "Contexto")}</span><span>{t("Direction", "Dirección")}</span><span>{t("Market", "Mercado")}</span></div>
            </div>
            <div className="method-stack">
              <article className="step" data-large="READ">
                <span className="num">{t("01 — Read", "01 — Leer")}</span>
                <div>
                  <h3><em className="it">{t("Signals", "Señales")}</em>{t(" before solutions.", " antes que soluciones.")}</h3>
                  <p>{t("Competitive landscape, cultural signals, category dynamics, consumer behavior, and market shifts.", "Panorama competitivo, señales culturales, dinámicas de categoría, comportamiento del consumidor y movimientos del mercado.")}</p>
                </div>
              </article>
              <article className="step" data-large="DEFINE">
                <span className="num">{t("02 — Define", "02 — Definir")}</span>
                <div>
                  <h3><em className="it">{t("Clarity", "Claridad")}</em>{t(" before execution.", " antes que ejecución.")}</h3>
                  <p>{t("Positioning, narrative, brand architecture, audience logic, and the decisions that give the brand a point of view.", "Posicionamiento, narrativa, arquitectura de marca, lógica de audiencia y las decisiones que dan a la marca un punto de vista.")}</p>
                </div>
              </article>
              <article className="step" data-large="GUIDE">
                <span className="num">{t("03 — Guide", "03 — Guiar")}</span>
                <div>
                  <h3><em className="it">{t("Direction", "Dirección")}</em>{t(" over noise.", " sobre ruido.")}</h3>
                  <p>{t("Messaging, launch roadmap, implementation guidance, optimization, and ongoing brand management.", "Mensaje, hoja de ruta de lanzamiento, guía de implementación, optimización y gestión de marca continua.")}</p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="subtle-section">
          <div className="section-chapter"><span className="roman">IV</span><span className="dot"></span></div>
          <div className="section-head">
            <h2>{t("Selected Experience", "Experiencia seleccionada")}</h2>
            <p className="statement">{t("Experience across global brands, regional markets, and founder-led ventures.", "Experiencia en marcas globales, mercados regionales y ventures liderados por fundadores.")}</p>
          </div>
          <div className="clients">Nike · Jordan · FILA · Vans · Converse · Kappa · Umbro · DC Shoes · Columbia · Merrell · Swatch · BodyTech · Sportline · Kicks · Hush Puppies · Quiksilver</div>
          <p className="founder-blurb" data-testid="founder-blurb">
            {t("B&B is led by ", "B&B está dirigida por ")}
            <strong>Wilhelm Noriega</strong>
            {t(", Brand Strategy Advisor. ", ", Brand Strategy Advisor. ")}
            <Link to="/wilhelm-noriega" className="founder-link" data-testid="founder-portfolio-link">{t("Portfolio →", "Portfolio →")}</Link>
          </p>
        </section>

        <section className="why-not-ai" data-testid="why-not-ai">
          <div className="wna-inner">
            <div className="kicker">{t("Why not just AI", "Por qué no solo IA")}</div>
            <h2>
              {t("AI can produce infinite output. It can't ", "La IA puede producir output infinito. No puede ")}
              <em className="it">{t("direct your brand", "dirigir tu marca")}</em>.
            </h2>
            <div className="wna-claim" data-testid="wna-claim">
              <p>
                {t("AI writes ten decent versions of anything. Choosing the one that fits your business, your category, and your audience — that's ", "La IA escribe diez versiones decentes de cualquier cosa. Elegir la que encaja con tu negocio, tu categoría y tu audiencia — eso es ")}
                <em className="it">{t("judgment", "criterio")}</em>.
              </p>
              <p>
                {t("A brand isn't a list of assets. It's a ", "Una marca no es una lista de assets. Es un ")}
                <em className="it">{t("universe", "universo")}</em>
                {t(" — one you take, defend, and translate into strategy, creativity and design.", " — uno que tomas, defiendes y traduces a estrategia, creatividad y diseño.")}
              </p>
            </div>
            <p className="wna-foot">
              {t(
                "We use AI where it saves time. We never let it make the call.",
                "Usamos IA donde ahorra tiempo. Nunca dejamos que ella tome la decisión."
              )}
            </p>
          </div>
        </section>

        <section className="cta subtle-section" id="contact">
          <div className="section-chapter"><span className="roman">V</span><span className="dot"></span><span>{t("Inquiry", "Consulta")}</span></div>
          <h2>{t("Understanding your brand, your market, and the real ", "Entender la marca, el mercado y el verdadero ")}<em className="it">{t("challenge", "desafío")}</em>.</h2>
          <div className="cta-row">
            <p className="small"></p>
            <Link to="/services" className="button" data-testid="about-cta-services">{t("Start a project", "Iniciar proyecto")}</Link>
          </div>
        </section>
      </div>
    </main>
  );
}
